/**
 * org-applications-list.ts
 *
 * Handles the AJAX behaviour for the Org Portal Application listing page:
 * - 300ms debounce on the search input (#searchTerm)
 * - Immediate fetch on select changes (#statusFilter, #gradeFilter, #academicYearFilter, #schoolFilter)
 * - Cancels in-flight requests using AbortController when a new input arrives
 * - Updates #orgApplicationsList innerHTML with the returned partial view HTML
 * - Keeps the browser URL in sync with window.history.replaceState
 * - Toggles the clear-filters button (#clearFiltersContainer) based on active filters
 *
 * Mirrors the pattern established in school-applications-list.ts.
 * Differences: adds #schoolFilter select; five inputs checked for active-filter state.
 */

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let activeController: AbortController | null = null;

/**
 * Fetches the org applications list partial from the server and replaces
 * the #applicationsList div content.
 * @param url - The request URL including query-string parameters.
 */
async function fetchOrgApplicationsList(url: string): Promise<void> {
  // Cancel any previous in-flight request
  if (activeController) {
    activeController.abort();
  }

  activeController = new AbortController();

  const listEl = document.getElementById('orgApplicationsList');
  if (!listEl) return;

  try {
    const response = await fetch(url, {
      signal: activeController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      console.error(`Org applications list fetch failed: ${response.status}`);
      return;
    }

    const html = await response.text();
    listEl.innerHTML = html;

    // Sync the browser address bar without a page reload
    window.history.replaceState(null, '', url);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Request was intentionally cancelled — no action needed
      return;
    }
    console.error('Unexpected error loading org applications list:', err);
  }
}

/**
 * Builds a URL for the org applications list with the current filter values.
 * Always resets pageNumber to 1 when a filter changes.
 */
function buildListUrl(): string {
  const listEl = document.getElementById('orgApplicationsList');
  const baseUrl = listEl?.dataset['listUrl'] ?? window.location.pathname;

  const searchInput  = document.getElementById('searchTerm')          as HTMLInputElement  | null;
  const statusSelect = document.getElementById('statusFilter')        as HTMLSelectElement | null;
  const gradeSelect  = document.getElementById('gradeFilter')         as HTMLSelectElement | null;
  const yearSelect   = document.getElementById('academicYearFilter')  as HTMLSelectElement | null;
  const schoolSelect = document.getElementById('schoolFilter')        as HTMLSelectElement | null;

  const params = new URLSearchParams();
  const search       = searchInput?.value.trim() ?? '';
  const status       = statusSelect?.value ?? '';
  const grade        = gradeSelect?.value ?? '';
  const academicYear = yearSelect?.value ?? '';
  const school       = schoolSelect?.value ?? '';

  if (search)        params.set('searchTerm',    search);
  if (status)        params.set('status',         status);
  if (grade)         params.set('grade',          grade);
  if (academicYear)  params.set('academicYearId', academicYear);
  if (school)        params.set('schoolId',       school);

  // Always reset to page 1 when a filter changes
  params.set('pageNumber', '1');

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Returns true when at least one filter input has a non-empty value.
 */
function hasActiveFilters(): boolean {
  const searchInput  = document.getElementById('searchTerm')          as HTMLInputElement  | null;
  const statusSelect = document.getElementById('statusFilter')        as HTMLSelectElement | null;
  const gradeSelect  = document.getElementById('gradeFilter')         as HTMLSelectElement | null;
  const yearSelect   = document.getElementById('academicYearFilter')  as HTMLSelectElement | null;
  const schoolSelect = document.getElementById('schoolFilter')        as HTMLSelectElement | null;

  return (searchInput?.value.trim()  ?? '') !== ''
      || (statusSelect?.value        ?? '') !== ''
      || (gradeSelect?.value         ?? '') !== ''
      || (yearSelect?.value          ?? '') !== ''
      || (schoolSelect?.value        ?? '') !== '';
}

/**
 * Shows or hides the clear-filters button based on whether any filter is active.
 * @param active - true when at least one filter has a non-empty value.
 */
function updateClearButton(active: boolean): void {
  const container = document.getElementById('clearFiltersContainer');
  if (!container) return;
  container.classList.toggle('d-none', !active);
}

/**
 * Initialises the org applications list AJAX behaviour.
 * Call once after the DOM is ready.
 * No-op when the required #applicationsList element is absent from the page.
 */
export function initOrgApplicationsList(): void {
  const listEl = document.getElementById('orgApplicationsList');
  if (!listEl) return;

  const searchInput  = document.getElementById('searchTerm')          as HTMLInputElement  | null;
  const statusSelect = document.getElementById('statusFilter')        as HTMLSelectElement | null;
  const gradeSelect  = document.getElementById('gradeFilter')         as HTMLSelectElement | null;
  const yearSelect   = document.getElementById('academicYearFilter')  as HTMLSelectElement | null;
  const schoolSelect = document.getElementById('schoolFilter')        as HTMLSelectElement | null;

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        const url = buildListUrl();
        updateClearButton(hasActiveFilters());
        void fetchOrgApplicationsList(url);
      }, 300);
    });
  }

  for (const select of [statusSelect, gradeSelect, yearSelect, schoolSelect]) {
    if (select) {
      select.addEventListener('change', () => {
        updateClearButton(hasActiveFilters());
        void fetchOrgApplicationsList(buildListUrl());
      });
    }
  }
}
